"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FinancialOpenApiOperations = void 0;
const errors_1 = require("../errors");
function errorCodesFor(endpoint) {
    return errors_1.FinancialApiErrorRegistry[endpoint]?.errors.map((entry) => entry.code) ?? [];
}
/**
 * One entry per endpoint contract this stage defines — 17 operations
 * across 15 named capability groups (Payments, Receipts, Advance,
 * Deposit, Refund, Tariff, Package Billing, Discount, Posting,
 * Approval, and 5 Billing operations covering Billing/Charge
 * Capture/Invoice/Corporate Billing/Insurance Billing). `path` is
 * illustrative REST shape for when a controller layer is eventually
 * built (this stage builds no routes) — Financial Application
 * Services already name the exact orchestration each of these would
 * call. `Financial Search` has no entry: no Application Service
 * exists to back it (re-confirmed, not just repeated, from Stage
 * 5C.2/5C.3's own identical conclusion) — see `FINANCIAL-API-REVIEW.md`.
 */
exports.FinancialOpenApiOperations = [
    {
        operationId: 'createPayment',
        method: 'POST',
        path: '/v1/financial/payments',
        summary: 'Create a payment (supports split payment via multiple lines)',
        version: 'v1',
        tags: ['Payments'],
        requestSchemaRef: 'CreatePaymentRequest',
        responseSchemaRef: 'CreatePaymentResponseDto',
        errorCodes: errorCodesFor('createPayment'),
    },
    {
        operationId: 'issueReceipt',
        method: 'POST',
        path: '/v1/financial/receipts',
        summary: 'Issue a receipt for a completed payment (Receipt -> Posting)',
        version: 'v1',
        tags: ['Receipts'],
        requestSchemaRef: 'IssueReceiptRequest',
        responseSchemaRef: 'IssueReceiptResponseDto',
        errorCodes: errorCodesFor('issueReceipt'),
    },
    {
        operationId: 'cancelReceipt',
        method: 'POST',
        path: '/v1/financial/receipts/{receiptId}/cancel',
        summary: 'Cancel an issued receipt',
        version: 'v1',
        tags: ['Receipts'],
        requestSchemaRef: 'CancelReceiptRequest',
        responseSchemaRef: 'CancelReceiptResponseDto',
        errorCodes: errorCodesFor('cancelReceipt'),
    },
    {
        operationId: 'creditAdvanceDeposit',
        method: 'POST',
        path: '/v1/financial/advance-deposit-accounts/{accountId}/credit',
        summary: 'Credit an advance or deposit account',
        version: 'v1',
        tags: ['Advance', 'Deposit'],
        requestSchemaRef: 'CreditAdvanceDepositRequest',
        responseSchemaRef: 'LedgerWorkflowResponseDto',
        errorCodes: errorCodesFor('creditAdvanceDeposit'),
    },
    {
        operationId: 'debitAdvanceDeposit',
        method: 'POST',
        path: '/v1/financial/advance-deposit-accounts/{accountId}/debit',
        summary: 'Debit an advance or deposit account against a charge',
        version: 'v1',
        tags: ['Advance', 'Deposit'],
        requestSchemaRef: 'DebitAdvanceDepositRequest',
        responseSchemaRef: 'LedgerWorkflowResponseDto',
        errorCodes: errorCodesFor('debitAdvanceDeposit'),
    },
    {
        operationId: 'settleDeposit',
        method: 'POST',
        path: '/v1/financial/advance-deposit-accounts/{accountId}/settle',
        summary: 'Settle a deposit account (IPD_DEPOSIT/CORPORATE_DEPOSIT only)',
        version: 'v1',
        tags: ['Deposit'],
        requestSchemaRef: 'SettleDepositRequest',
        responseSchemaRef: 'LedgerWorkflowResponseDto',
        errorCodes: errorCodesFor('settleDeposit'),
    },
    {
        operationId: 'processRefund',
        method: 'POST',
        path: '/v1/financial/refunds',
        summary: 'Process a refund against an issued receipt (Refund -> Validation -> Posting)',
        version: 'v1',
        tags: ['Refund'],
        requestSchemaRef: 'ProcessRefundRequest',
        responseSchemaRef: 'ProcessRefundResponseDto',
        errorCodes: errorCodesFor('processRefund'),
    },
    {
        operationId: 'activateTariff',
        method: 'POST',
        path: '/v1/financial/tariffs/{tariffId}/activate',
        summary: 'Activate a DRAFT tariff',
        version: 'v1',
        tags: ['Billing'],
        requestSchemaRef: 'ActivateTariffRequest',
        responseSchemaRef: 'ActivateTariffResponseDto',
        errorCodes: errorCodesFor('activateTariff'),
    },
    {
        operationId: 'activatePackage',
        method: 'POST',
        path: '/v1/financial/packages/{packageId}/activate',
        summary: 'Activate a DRAFT package',
        version: 'v1',
        tags: ['Package Billing'],
        requestSchemaRef: 'ActivatePackageRequest',
        responseSchemaRef: 'ActivatePackageResponseDto',
        errorCodes: errorCodesFor('activatePackage'),
    },
    {
        operationId: 'applyDiscount',
        method: 'POST',
        path: '/v1/financial/discount-rules/{ruleId}/apply',
        summary: 'Apply a discount rule against a base amount (Discount -> Posting)',
        version: 'v1',
        tags: ['Discount'],
        requestSchemaRef: 'ApplyDiscountRequest',
        responseSchemaRef: 'ApplyDiscountResponseDto',
        errorCodes: errorCodesFor('applyDiscount'),
    },
    {
        operationId: 'recordPostingEvent',
        method: 'POST',
        path: '/v1/financial/posting-events',
        summary: 'Record that a Financial Event occurred, resolving (not executing) the matching posting rule',
        version: 'v1',
        tags: ['Posting'],
        requestSchemaRef: 'RecordPostingEventRequest',
        responseSchemaRef: 'RecordPostingEventResponseDto',
        errorCodes: errorCodesFor('recordPostingEvent'),
    },
    {
        operationId: 'resolveApprovalChain',
        method: 'GET',
        path: '/v1/financial/approval-chains/{moduleName}/{actionName}',
        summary: 'Resolve the approval chain (who must approve, in what order) for a module/action — chain resolution only, no approval-state tracking',
        version: 'v1',
        tags: ['Approval'],
        requestSchemaRef: 'ResolveApprovalChainRequest',
        responseSchemaRef: 'ResolveApprovalChainResponseDto',
        errorCodes: errorCodesFor('resolveApprovalChain'),
    },
    {
        operationId: 'computeChargeBilling',
        method: 'POST',
        path: '/v1/financial/billing/compute',
        summary: 'Charge -> Discount -> GST -> computed billing summary ("Invoice" — never persisted, no Invoice entity exists)',
        version: 'v1',
        tags: ['Billing', 'Charge Capture', 'Invoice'],
        requestSchemaRef: 'ComputeChargeBillingRequest',
        responseSchemaRef: 'ComputeChargeBillingResponseDto',
        errorCodes: errorCodesFor('computeChargeBilling'),
    },
    {
        operationId: 'billAgainstAdvance',
        method: 'POST',
        path: '/v1/financial/billing/bill-against-advance',
        summary: 'Advance -> Billing: compute a charge and debit it from an advance/deposit account',
        version: 'v1',
        tags: ['Billing', 'Advance'],
        requestSchemaRef: 'BillAgainstAdvanceRequest',
        responseSchemaRef: 'BillAgainstAdvanceResponseDto',
        errorCodes: errorCodesFor('billAgainstAdvance'),
    },
    {
        operationId: 'billAndCreatePayment',
        method: 'POST',
        path: '/v1/financial/billing/payments',
        summary: 'Charge -> Discount -> GST -> Payment, ending in a real FinPayment',
        version: 'v1',
        tags: ['Billing', 'Payments'],
        requestSchemaRef: 'BillAndCreatePaymentRequest',
        responseSchemaRef: 'BillAndCreatePaymentResponseDto',
        errorCodes: errorCodesFor('billAndCreatePayment'),
    },
    {
        operationId: 'processCorporateBilling',
        method: 'POST',
        path: '/v1/financial/billing/corporate',
        summary: 'Corporate Billing — same orchestration as billAndCreatePayment, caller supplies the corporate tariff',
        version: 'v1',
        tags: ['Corporate Billing'],
        requestSchemaRef: 'ProcessCorporateBillingRequest',
        responseSchemaRef: 'BillAndCreatePaymentResponseDto',
        errorCodes: errorCodesFor('processCorporateBilling'),
    },
    {
        operationId: 'processInsuranceBilling',
        method: 'POST',
        path: '/v1/financial/billing/insurance',
        summary: 'Insurance Billing — same orchestration as billAndCreatePayment; does not verify PatientCoverage eligibility',
        version: 'v1',
        tags: ['Insurance Billing'],
        requestSchemaRef: 'ProcessInsuranceBillingRequest',
        responseSchemaRef: 'BillAndCreatePaymentResponseDto',
        errorCodes: errorCodesFor('processInsuranceBilling'),
    },
];
//# sourceMappingURL=financial.operations.js.map